import {useFeatureAppEnv, useFeatureFlags} from '@smart/feature-app';
import type {
  FinanceParamsV1,
  LeasingParamsV1,
  OpenCalculatorListenerV1,
  PaymentConfigV1,
} from '@smart/payment-config-service';
import type {PriceTypeV1} from '@smart/price-preferences-provider';
import {Flex, Text} from '@smart/react-components';
import {usePriceType} from '@smart/react-utils';
import {Notification, Radio, Tabs} from '@smart/web-components';
import * as React from 'react';
import {useState} from 'react';
import {createPortal} from 'react-dom';
import type {
  FinanceOptions,
  LeasingOptions,
  LeasingOptionsParams,
} from '../../bff/app-model/create-app-model';
import type {LeasingContent} from '../../bff/app-model/fetch-app-model';
import {FEATURE_FLAG} from '../constants';
import {useAppModelContext} from '../hooks/use-app-model-context';
import {useLocale} from '../hooks/use-locale';
import {usePriceDetails} from '../hooks/use-price-details';
import {trackApply} from '../utils/track-apply';
import {CashBuy} from './cash-buy';
import {Finance} from './finance';
import {LearnMoreText} from './learn-more-text';
import styles from './price-type-radios.module.scss';

export interface CalculatorProps {
  readonly openCalculatorListenerArgs: Parameters<OpenCalculatorListenerV1>;
  readonly headerRef: React.RefObject<HTMLElement>;
  readonly onApply: () => void;
}

const FALLBACK_PAYMENT_TYPE = `leasing`;
const FALLBACK_PAYMENT_TYPE_UK = `cash-buy`;

// eslint-disable-next-line complexity
export function Calculator({
  openCalculatorListenerArgs: [
    {calculatorConfig, purchaseItem},
    closeCalculator,
    onChange,
  ],
  headerRef,
  onApply,
}: CalculatorProps) {
  const {featureServices} = useFeatureAppEnv();
  const {
    'adb-context-service': adbContextService,
    'endpoint-directory': endpointDirectory,
    'login-service': loginService,
    'price-preferences-provider': pricePreferencesProvider,
  } = featureServices;

  const {appModel} = useAppModelContext();
  const {content, options: _options} = appModel;
  const locale = useLocale();
  const isLoggedIn = loginService.loginState.status === `logged-in`;
  const isADBAgent = adbContextService?.adbContext?.hasCustomer;

  const {enabled: enableAnonymousFlow} = useFeatureFlags(
    FEATURE_FLAG.ENABLE_ANONYMOUS_FLOW,
  );

  const [priceType, setPriceType] = usePriceType({
    adbContextService,
    loginService,
    pricePreferencesProvider,
  });

  // show price type for:
  // anonymous for all payment types (should be shown in shopping cart)
  // ADB agent for PN18 (should not be shown in the shopping cart)
  const isShownToAnonymous =
    enableAnonymousFlow && !isLoggedIn && purchaseItem.type !== `none`;
  const isShownToAnonymousInSalesTools =
    !isLoggedIn && purchaseItem.type === `pn18`;
  const isShownToADB = isADBAgent && purchaseItem.type === `pn18`;
  const showPriceTypeToggle =
    !!setPriceType &&
    headerRef.current &&
    (isShownToAnonymous || isShownToADB || isShownToAnonymousInSalesTools);

  const [options, setOptions] = useState(
    _options.map((o) => {
      if (o.paymentType === calculatorConfig?.mode) {
        return {
          ...o,
          params: calculatorConfig?.params,
        };
      }
      return o;
    }),
  );

  const [mode, setMode] = useState(
    calculatorConfig?.mode ??
      //content.tabs[0]?.paymentModes[0]?.mode ?? //this should be re-enable once PCP is default UK market.
      (locale.marketId === `gb`
        ? FALLBACK_PAYMENT_TYPE_UK
        : FALLBACK_PAYMENT_TYPE),
  );

  const handleOnOption = (key: string, value: string) => {
    const idx = options.findIndex(({paymentType}) => paymentType === mode);
    if (idx < 0) return;
    setOptions(
      options.map((o, i) =>
        i === idx
          ? ({
              ...o,
              params: {
                ...o.params,
                [key]: isNaN(value as any) ? value : parseInt(value),
              },
            } as LeasingOptions | FinanceOptions)
          : o,
      ),
    );
  };
  const selectedOption = options.find(({paymentType}) => paymentType === mode);

  const [priceDetailsResult, fetchPriceDetails, resetPrice] = usePriceDetails({
    purchaseItem,
    params:
      selectedOption?.params ??
      options.find((option) => option.paymentType === `leasing`)?.params ??
      ({} as LeasingParamsV1 | FinanceParamsV1),
    priceType: priceType ?? `private`,
  });

  const getPaymentConfig = () => {
    const calculatorParams: Record<string, any> = {
      mode,
    };
    if (selectedOption) {
      calculatorParams.params = selectedOption?.params;
      // Backwards compatability
      if (calculatorParams.mode === `leasing`) {
        calculatorParams.leasingParams = selectedOption.params;
      }
    }
    return calculatorParams as PaymentConfigV1;
  };

  const isMountRef = React.useRef(true);

  React.useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    resetPrice();
    const pendingOnChange = onChange(getPaymentConfig(), `change`);

    if (pendingOnChange instanceof Promise) {
      pendingOnChange
        .then(() => fetchPriceDetails())
        .catch(() => {
          // error?
        });
    } else {
      fetchPriceDetails();
    }
  }, [JSON.stringify({mode, selectedOption})]);

  React.useEffect(() => {
    fetchPriceDetails();
  }, []);

  const handleApply = React.useCallback(() => {
    const calculatorParams = getPaymentConfig();

    closeCalculator(calculatorParams as PaymentConfigV1, `submit`)?.catch(
      () => {
        // error?
      },
    );

    pricePreferencesProvider?.setPriceType(priceType ?? `private`);

    onApply();

    trackApply({
      envName: endpointDirectory?.envName ?? `stage`,
      params: (calculatorParams as any).params ?? {},
      locale,
      loginState: loginService?.loginState,
      mode: mode as any,
      priceDetails:
        priceDetailsResult.status === `completed` &&
        `priceDetails` in priceDetailsResult.state
          ? priceDetailsResult.state.priceDetails
          : undefined,
      priceType: priceType ?? `private`,
    });
  }, [
    mode,
    locale,
    JSON.stringify(priceDetailsResult),
    priceType,
    closeCalculator,
    onApply,
    JSON.stringify(options),
  ]);

  const {learnMoreText} = content;

  const hasError = React.useMemo(
    () =>
      priceDetailsResult.status !== `completed` ||
      (priceDetailsResult.status === `completed` &&
        `error` in priceDetailsResult.state),
    [priceDetailsResult.status],
  );
  const paymentTypeExplanation = (
    content.tabs
      .find((t) => t.customerType.name === priceType)
      ?.paymentModes.find((p) => p.mode === mode) as LeasingContent
  )?.paymentTypeExplanation;

  const tabItems = content.tabs.map((tab, index) => {
    if (tab.customerType.name !== priceType) return null;

    if (!tab.paymentModes.find((paymentMode) => paymentMode.mode === mode))
      setMode(tab.paymentModes[0]?.mode ?? `leasing`);
    return (
      <Tabs
        key={tab.paymentModes[index]?.paymentDetailsText || index}
        items={tab.paymentModes.map((paymentMode) => {
          const getContentForPaymentMode = () => {
            if (paymentMode.mode === `cash-buy`) {
              return (
                <CashBuy
                  hasError={hasError}
                  priceDetailsResult={priceDetailsResult}
                  priceType={priceType ?? `private`}
                  onApply={handleApply}
                  content={paymentMode}
                />
              );
            }

            const tabOptions = options.find(
              ({paymentType}) => paymentType === mode,
            );
            return (
              <Finance
                hasError={hasError}
                content={paymentMode as LeasingContent}
                params={tabOptions?.params ?? ({} as LeasingOptionsParams)}
                options={
                  (tabOptions as LeasingOptions | FinanceOptions) ??
                  ({} as LeasingOptions | FinanceOptions)
                }
                priceDetailsResult={priceDetailsResult}
                priceType={priceType ?? `private`}
                paymentMode={paymentMode.mode}
                onParamsChange={handleOnOption}
                onApply={handleApply}
              />
            );
          };

          return {
            tabId: paymentMode.mode,
            headline: paymentMode.tabItem.headline,
            disabled: priceDetailsResult.status !== `completed`,
            content: getContentForPaymentMode(),
            id: paymentMode.mode,
            onClick: () => setMode(paymentMode.mode),
          };
        })}
        initialTab={Math.max(
          tab.paymentModes.findIndex((tabItem) => tabItem.mode === mode),
          0,
        )}
      />
    );
  });

  const errorMessage = React.useMemo(() => {
    const matchTab = content.tabs.find(
      (tab) => tab?.customerType?.name === priceType,
    );
    if (matchTab) {
      const paymentModel = matchTab?.paymentModes.find(
        (item) => item.mode === mode,
      );

      if (paymentModel?.mode === `leasing`) {
        return {
          title: paymentModel?.monthFeeFetchingErrorTitle || ``,
          message: paymentModel.monthlyFeeFetchError || ``,
        };
      } else if (paymentModel?.mode === `cash-buy`) {
        return {
          title: ``,
          message: paymentModel?.totalPriceFetchError || ``,
        };
      }
    }
    return;
  }, [content.tabs, mode, priceType]);

  return (
    <>
      {headerRef.current &&
        createPortal(
          <div className={styles.radios}>
            {priceDetailsResult.status === `completed` &&
              `error` in priceDetailsResult.state &&
              errorMessage && (
                <>
                  <Notification
                    className={styles.notification}
                    variant="warning"
                    text={
                      <div>
                        <Text as="p" variant="lbl-100">
                          {errorMessage.title}
                        </Text>
                        <Text as="p" variant="p-100">
                          {errorMessage.message}
                        </Text>
                      </div>
                    }
                    closeButton={{
                      ariaLabel: `Close`,
                      onClick: fetchPriceDetails,
                    }}
                  />
                </>
              )}
            {showPriceTypeToggle &&
              content.tabs.map(({customerType}) => (
                <Radio
                  key={customerType.name ?? ``}
                  id={customerType.name ?? ``}
                  label={customerType.label}
                  value={customerType.name}
                  checked={priceType === customerType.name}
                  disabled={priceDetailsResult.status !== `completed`}
                  onChange={() => {
                    setPriceType(customerType.name as PriceTypeV1);
                    const calculatorParams = getPaymentConfig();
                    closeCalculator(
                      calculatorParams as PaymentConfigV1,
                      `submit`,
                    )?.catch(() => {
                      // error?
                    });
                  }}
                />
              ))}
          </div>,
          headerRef.current,
        )}
      <Flex gap={400} direction={`column`}>
        <Flex gap={200} direction={`column`}>
          {learnMoreText && <LearnMoreText>{learnMoreText}</LearnMoreText>}
          {paymentTypeExplanation && (
            <Text variant="cap-200" color={`c-mid-contrast`}>
              {paymentTypeExplanation}
            </Text>
          )}
        </Flex>
        {tabItems}
      </Flex>
    </>
  );
}
